import type { SurveyResponse } from '@/data/types/survey-response'
import { ApiService } from './api/api'
import type { Journey } from '@/data/schema/journey'

export default class JourneyService {
	private apiService

	constructor() {
		this.apiService = new ApiService(true)
	}

	/**
	 * Get a journey record by ID
	 * @param id string - collection number
	 * @param params Object
	 * @returns Promise
	 */
	get(id: string, params: Object): Promise<any> {
		return this.apiService.get(`/journey/${id}`, {
			params: params
		})
	}

	/**
	 * Get journey planner journeys by depots
	 * @param depots string - depot codes
	 * @param params Object
	 * @returns Promise
	 */
	getJourneyPlannerJourneys(depots: string, params: Object): Promise<any> {
		return this.apiService.get(`service/journey-planner/${depots}`, {
			params: params
		})
	}

	/**
	 * Get driver app
	 * @param depot string - depot code
	 * @param params object
	 * @returns Promise
	 */
	getDriverAppJourneys(depot: string, params: Object): Promise<any> {
		return this.apiService.get(`service/driver-app/journey/${depot}`, {
			params: params
		})
	}

	/**
	 * Update Journey params
	 * @param id string
	 * @param params Object
	 * @returns Promise
	 */
	updateJourney(id: string, params: Object): Promise<any> {
		return this.apiService.patch(`/journey/${id}`, params)
	}

	/**
	 * Mark the journey as complete
	 * @param id string
	 * @returns Promise
	 */
	completeJourney(id: string): Promise<any> {
		return this.apiService.patch(`/journey/${id}/complete`)
	}

	/**
	 * Bulk update collection items
	 * @param items
	 * @returns
	 */
	updateItems(items: Partial<Journey>[]): Promise<any> {
		return this.apiService.patch(`/journey/bulk-update`, {
			items: items
		})
	}

	/**
	 * Get the survey responses for this journey
	 * @param journey_id
	 * @returns Promise
	 */
	getSurvey(journey_id: number | string): Promise<any> {
		return this.apiService.get(`/survey-answers/journey/${journey_id}`)
	}

	/**
	 * Save the survey responses
	 * @param journey_id number
	 * @param survey SurveyResponse
	 * @returns Promise
	 */
	saveSurvey(journey_id: number | string, survey: SurveyResponse[]): Promise<any> {
		return this.apiService.post(`/survey-answers`, {
			type: 'journey',
			id: journey_id,
			items: survey
		})
	}

	/**
	 * Get all files related to the journey
	 * @param journey_id
	 * @returns Promise
	 */
	getFiles(journey_id: number | string): Promise<any> {
		return this.apiService.get(`/journey/${journey_id}/photo`)
	}

	/**
	 * Save journey photos
	 * @param journey_id
	 * @param files
	 * @returns Promise
	 */
	savePhotos(journey_id: number | string, files: File[]): Promise<any> {
		const formData = new FormData()

		files.forEach((file, index) => {
			formData.append(`photo[${index}]`, file)
		})

		return this.apiService.post(`/journey/${journey_id}/photo`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})
	}

	/**
	 * Save the signature image
	 * @param journey_id
	 * @param signature
	 * @returns Promise
	 */
	saveSignature(journey_id: number | string, signature: string): Promise<any> {
		return this.apiService.post(`/journey/${journey_id}/signature`, { signature })
	}

	/**
	 * Search for files related to a journey
	 * @param search_term string - contract or collection number
	 * @returns Promise
	 */
	searchJourneyFiles(search_term: string): Promise<any> {
		return this.apiService.post('/journey/search-files', { search_term })
	}
}
